import { useState, useCallback, useContext } from 'react';
import {
    sendWhatsAppMessage,
    sendOrderConfirmation,
    sendShippingUpdate,
    sendDeliveryConfirmation,
    sendCancellationMessage,
    sendPaymentReminder,
    normalizePhoneNumber,
} from '../../services/fontteService';
import { SettingsContext } from '../../contexts/SettingsContext';
import { resolveWhatsAppConfig } from './config';
import type { UseWhatsAppResult, UseWhatsAppState } from './types';

export function useWhatsApp(): UseWhatsAppResult {
    const { whatsappSettings } = useContext(SettingsContext);
    const resolveDevice = useCallback((explicit?: string, brandId?: string, csId?: string): string | undefined => {
        if (explicit) return explicit;
        if (brandId && whatsappSettings?.brandDeviceMap && whatsappSettings.brandDeviceMap[brandId]) return whatsappSettings.brandDeviceMap[brandId];
        if (csId && (whatsappSettings as any)?.csDeviceMap && (whatsappSettings as any).csDeviceMap[csId]) return (whatsappSettings as any).csDeviceMap[csId];
        return whatsappSettings?.defaultDevice || undefined;
    }, [whatsappSettings]);
    const [state, setState] = useState<UseWhatsAppState>({ isLoading: false, error: null, success: false });

    const handleResponse = useCallback((response: any) => {
        if (response.status) setState({ isLoading: false, error: null, success: true });
        else setState({ isLoading: false, error: response.message || 'Unknown error', success: false });
        return response;
    }, []);

    const sendMessage = useCallback(async (phoneNumber: string, message: string, options?: { brandId?: string; csId?: string; device?: string }) => {
        setState({ isLoading: true, error: null, success: false });
        const config = await resolveWhatsAppConfig(options?.brandId, options?.csId);
        const device = options?.device || config?.device || resolveDevice(options?.device, options?.brandId, options?.csId);
        const response = await sendWhatsAppMessage(phoneNumber, message, '62', device, config?.apiKey);
        return handleResponse(response);
    }, [handleResponse, resolveDevice]);

    const handleSendOrderConfirmation = useCallback(async (phoneNumber: string, orderData: any, options?: any) => {
        setState({ isLoading: true, error: null, success: false });
        const response = await sendOrderConfirmation(phoneNumber, orderData, { device: resolveDevice(options?.device, options?.brandId, options?.csId) });
        return handleResponse(response);
    }, [handleResponse, resolveDevice]);

    const handleSendShippingUpdate = useCallback(async (phoneNumber: string, orderData: any, options?: any) => {
        setState({ isLoading: true, error: null, success: false });
        const response = await sendShippingUpdate(phoneNumber, orderData, { device: resolveDevice(options?.device, options?.brandId, options?.csId) });
        return handleResponse(response);
    }, [handleResponse, resolveDevice]);

    const handleSendDeliveryConfirmation = useCallback(async (phoneNumber: string, orderData: any, options?: any) => {
        setState({ isLoading: true, error: null, success: false });
        const response = await sendDeliveryConfirmation(phoneNumber, orderData, { device: resolveDevice(options?.device, options?.brandId, options?.csId) });
        return handleResponse(response);
    }, [handleResponse, resolveDevice]);

    const handleSendCancellationMessage = useCallback(async (phoneNumber: string, orderData: any, options?: any) => {
        setState({ isLoading: true, error: null, success: false });
        const response = await sendCancellationMessage(phoneNumber, orderData, { device: resolveDevice(options?.device, options?.brandId, options?.csId) });
        return handleResponse(response);
    }, [handleResponse, resolveDevice]);

    const handleSendPaymentReminder = useCallback(async (phoneNumber: string, orderData: any, _options?: any) => {
        setState({ isLoading: true, error: null, success: false });
        const response = await sendPaymentReminder({ phone: phoneNumber, customerName: orderData.customerName, productTitle: orderData.productTitle, totalPrice: orderData.totalPrice, orderId: orderData.orderId, paymentUrl: orderData.paymentUrl });
        return handleResponse(response);
    }, [handleResponse]);

    const clearError = useCallback(() => setState(prev => ({ ...prev, error: null })), []);
    const normalizePhone = useCallback((phone: string, countryCode: string = '62') => normalizePhoneNumber(phone, countryCode), []);

    return {
        ...state,
        sendMessage,
        sendOrderConfirmation: handleSendOrderConfirmation,
        sendShippingUpdate: handleSendShippingUpdate,
        sendDeliveryConfirmation: handleSendDeliveryConfirmation,
        sendCancellationMessage: handleSendCancellationMessage,
        sendPaymentReminder: handleSendPaymentReminder,
        clearError,
        normalizePhone,
    };
}
